<script lang="ts">
  import { onMount } from "svelte";
  import { storyblokEditable } from "@storyblok/svelte";
  import { deviceDetails } from "$lib/consumer/stores/deviceDetails";
  import GetDeliveryButton from "./GetDeliveryButton.svelte";
  import gsap from "gsap";

  // Get the block data
  export let blok;

  // Get the device details
  $: ({ isMobile } = $deviceDetails);

  // References for the animation elements
  let nowFlyingSection: HTMLElement;
  let littleZip: HTMLElement;

  /**
   * Increases the section background image scaling as the viewer scrolls by
   */
  function animateBackgroundImage() {
    const backgroundImageTween = gsap.fromTo(
      nowFlyingSection,
      {
        backgroundSize: isMobile ? "180% auto" : "160% auto",
      },
      {
        scrollTrigger: {
          trigger: nowFlyingSection,
          start: "top 80%",
          end: "bottom center",
          scrub: 1,
        },
        backgroundSize: "110% auto",
        ease: "none",
      },
    );
  }

  /**
   * Animates the zip flying from bottom left to top right
   */
  function animateZipFlying() {
    const zipFlyingTween = gsap.fromTo(
      littleZip,
      {
        // Starting position (bottom left)
        top: isMobile ? "60vh" : "60vh",
        left: isMobile ? "30vw" : "55vw",
      },
      {
        // Ending position (top right)
        scrollTrigger: {
          trigger: nowFlyingSection,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
        top: isMobile ? "40vh" : "50vh",
        left: isMobile ? "70vw" : "65vw",
        ease: "power1.inOut",
      },
    );
  }

  onMount(() => {
    // Initialize animations
    const gsapContext = gsap.context(() => {
      animateBackgroundImage();
      animateZipFlying();
    });

    // Re-initialize animations on horizontal resize only
    let previousWidth = window.innerWidth;
    function handleWindowResize() {
      if (window.innerWidth !== previousWidth) {
        previousWidth = window.innerWidth;

        // Cleanup animations
        gsapContext.revert();

        // Re-initialize animations
        animateBackgroundImage();
        animateZipFlying();
      }
    }
    window.addEventListener("resize", handleWindowResize);

    // Unmount
    return () => {
      // Remove event listeners and animations
      window.removeEventListener("resize", handleWindowResize);
      gsapContext.revert();
    };
  });
</script>

<section
  class="now-flying-section mx-auto"
  bind:this={nowFlyingSection}
  use:storyblokEditable={blok}
>
  <div class="now-flying-content">
    <h1 class="main-heading type:h3">{blok.heading}</h1>

    <div class="locations">
      {#each blok.locations as location}
        <div class="location" style="color: {location.color.color}">
          <h5 class="location-state type:h5-eyebrow">{location.state}</h5>
          <h1 class="location-region type:h3">{location.region}</h1>
        </div>
      {/each}
    </div>

    <h2 class="heading-coming-soon type:h3">{blok.headingComingSoon}</h2>

    <div class="locations">
      {#each blok.locationsComingSoon as location}
        <div class="location" style="color: {location.color.color}">
          <h5 class="location-state type:h5-eyebrow">{location.state}</h5>
          <h1 class="location-region type:h3">{location.region}</h1>
        </div>
      {/each}
    </div>

    <GetDeliveryButton {blok} />
  </div>

  <div class="little-zip-container">
    <img
      class="little-zip"
      src="/images/home/nowflying/nowflying-zip.webp"
      alt="Zip flying in the clouds"
      bind:this={littleZip}
    />
  </div>
</section>

<style lang="postcss">
  .now-flying-section {
    position: relative;
    margin-top: 2rem;
    padding-top: 4rem;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    padding-bottom: 3rem;
    background-image: url("/images/home/nowflying/nowflying-background-mobile.webp");
    background-repeat: no-repeat;
    background-position: center center;
    background-color: #56c1e1;
    overflow: hidden;

    @screen lg {
      margin-top: 6rem;
      padding-top: 5rem;
      padding-bottom: 5rem;
      background-image: url("/images/home/nowflying/nowflying-background-desktop.webp");
    }
  }

  .now-flying-content {
    position: relative;
    display: grid;
    justify-content: center;
    z-index: 2;
  }

  .little-zip-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  }

  .little-zip {
    position: absolute;
    width: 50px;
    height: auto;
    opacity: 0.8;

    @screen md {
      width: 80px;
    }
  }

  .main-heading {
    text-align: center;
    color: #365cd1;
  }

  .heading-coming-soon {
    margin-top: 2rem;
    text-align: center;
    color: #8d5ac9;

    @screen lg {
      margin-top: 4rem;
    }
  }

  .locations {
    margin-top: 2rem;
    @media (min-width: 768px) {
      margin-top: 3rem;
    }
  }

  .location {
    margin-bottom: 1rem;
  }

  .location-state,
  .location-region {
    color: inherit;
  }

  .location-state {
    @media (min-width: 768px) {
      font-size: 16px;
    }
  }

  .location-region {
    font-size: 52px;

    @media (min-width: 768px) {
      font-size: 96px;
    }
  }

  /* Override the check availability button component styles */
  .now-flying-section :global(.get-delivery-button-wrapper) {
    margin-top: 2rem;
    margin-left: auto;
    margin-right: auto;

    @media (min-width: 768px) {
      margin-top: 3rem;
    }
  }
</style>
